import React, { useState } from 'react'
import {
  Box,
  Image,
  Badge,
  Text,
  Flex,
  Heading,
  Tag,
  TagLabel,
  Tooltip,
  VStack,
  HStack,
  useColorModeValue,
  Divider,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react'
import { InfoIcon, DeleteIcon } from '@chakra-ui/icons'

export const ProdutoCard = ({ produto, onClick, onDesativar, isDesativando }) => {
  const cardBgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const cancelRef = React.useRef()

  const hasValidImage = produto.imagem_prod_store || produto.img_home_produto

  const getPrecoDezDias = () => {
    if (!produto.preco_dias || !produto.preco_dias.length) return null

    const precoDezDias = produto.preco_dias.find((item) => parseInt(item.prazo) === 10 || item.prazo === '10')

    if (precoDezDias && precoDezDias.preco) {
      return `R$ ${Number(precoDezDias.preco).toFixed(2)}`
    }

    return null
  }

  const handleDesativarClick = (e) => {
    e.stopPropagation()
    setIsConfirmOpen(true)
  }

  const handleConfirmDesativar = () => {
    setIsConfirmOpen(false)
    onDesativar(produto.codigo_produto)
  }

  const handleCancelDesativar = () => {
    setIsConfirmOpen(false)
  }

  return (
    <Box
      width="320px"
      height="400px"
      position="relative"
      onClick={() => !isDesativando && onClick && onClick(produto)}
      cursor={onClick && !isDesativando ? 'pointer' : 'default'}
      transition="transform 0.2s ease-in-out"
      _hover={{ transform: 'translateY(-4px)' }}
    >
      <Box
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        bg={cardBgColor}
        shadow="sm"
        h="100%"
        _hover={{ shadow: 'md', borderColor: 'blue.300' }}
        transition="all 0.2s"
        overflow="hidden"
        position="relative"
        opacity={isDesativando ? 0.7 : 1}
      >
        {/* Imagem */}
        <Skeleton isLoaded={!isDesativando} height="200px">
          <Box position="relative" height="200px" overflow="hidden" bg="gray.100">
            <Image
              src={hasValidImage ? produto.img_home_produto : 'https://via.placeholder.com/300x200?text=Sem+Imagem'}
              alt={produto.nome}
              w="100%"
              h="100%"
              objectFit="cover"
            />

            {produto.ultrarapido === 'S' && (
              <Badge position="absolute" top={2} left={2} colorScheme="green" variant="solid" borderRadius="md" px={2} py={1} fontSize="xs">
                Ultra Rápido
              </Badge>
            )}
          </Box>
        </Skeleton>

        {/* Conteúdo */}
        <VStack p={3} spacing={2} align="stretch" h="180px">
          {/* Título e Código */}
          <SkeletonText isLoaded={!isDesativando} noOfLines={2} spacing={2}>
            <Box title={produto.nome}>
              <Heading as="h3" size="sm" noOfLines={1} lineHeight="tight" title={produto.nome}>
                {produto.nome}
              </Heading>

              <HStack mt={1} spacing={1}>
                <Badge colorScheme="blue" fontSize="xs">
                  Cód: {produto.codigo_produto}
                </Badge>
              </HStack>
            </Box>
          </SkeletonText>

          <Divider />

          {/* Informações principais */}
          <SkeletonText isLoaded={!isDesativando} noOfLines={4} spacing={2}>
            <Box>
              <HStack mb={1}>
                <Text fontSize="xs" fontWeight="bold" width="80px">
                  Estoque:
                </Text>
                <Tag size="sm" variant="subtle" colorScheme={parseInt(produto.estoque_disponivel) > 1000 ? 'green' : 'orange'}>
                  <TagLabel>{produto.estoque_disponivel}</TagLabel>
                </Tag>
              </HStack>

              <HStack mb={1}>
                <Text fontSize="xs" fontWeight="bold" width="80px">
                  Prazo:
                </Text>
                <Text fontSize="xs">{produto.prazo_minimo_entrega} dias</Text>
              </HStack>

              <HStack mb={1}>
                <Text fontSize="xs" fontWeight="bold" width="80px">
                  Batidas:
                </Text>
                <Text fontSize="xs">{produto.batidadas_maximas}</Text>
              </HStack>

              {/* Adicionando preço para 10 dias */}
              <HStack>
                <Text fontSize="xs" fontWeight="bold" width="80px">
                  Valor (10d):
                </Text>
                <Text fontSize="xs" color={getPrecoDezDias() ? 'green.600' : 'gray.500'} fontWeight="medium">
                  {getPrecoDezDias() || 'Sob consulta'}
                </Text>
              </HStack>
            </Box>
          </SkeletonText>

          {/* Cores disponíveis */}
          {produto.cores && produto.cores.length > 0 && (
            <Skeleton isLoaded={!isDesativando}>
              <HStack spacing={1} mt="auto">
                <Text fontSize="xs" fontWeight="bold">
                  Cores:
                </Text>
                <Flex gap={1} flexWrap="wrap">
                  {produto.cores.map((cor) => (
                    <Tooltip key={cor.cod_cor} label={`${cor.name} (${cor.quantidade_disponivel})`}>
                      <Box w="15px" h="15px" borderRadius="full" bg={cor.hex} borderWidth="1px" borderColor="gray.300" />
                    </Tooltip>
                  ))}
                </Flex>
              </HStack>
            </Skeleton>
          )}
        </VStack>

        {/* Botões de ação */}
        <Skeleton isLoaded={!isDesativando} position="absolute" bottom={2} right={2}>
          <HStack spacing={1}>
            {/* Botão desativar */}
            <Tooltip label="Desativar produto">
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={handleDesativarClick}
                aria-label="Desativar produto"
                isDisabled={isDesativando}
              />
            </Tooltip>

            {/* Botão de informações */}
            <Tooltip label="Ver detalhes completos">
              <IconButton
                icon={<InfoIcon />}
                size="sm"
                colorScheme="blue"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  onClick && onClick(produto)
                }}
                aria-label="Ver detalhes"
                isDisabled={isDesativando}
              />
            </Tooltip>
          </HStack>
        </Skeleton>
      </Box>

      {/* Dialog de confirmação */}
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={handleCancelDesativar} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Desativar Produto
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text>
                Tem certeza que deseja <strong>desativar</strong> o produto "{produto.nome}" (Cód: {produto.codigo_produto})?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                O produto ficará indisponível para visualização e venda no site.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancelDesativar}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDesativar} ml={3} isLoading={isDesativando === produto.codigo_produto}>
                Desativar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
